import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Paper,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import GavelIcon from '@mui/icons-material/Gavel';
import EmailIcon from '@mui/icons-material/Email';
import { rentalSearch } from '../utils/rentalSearch';
import FeaturedListingsSection from '../components/home/FeaturedListingsSection';

const features = [
  {
    title: 'Find Your Perfect Home',
    description: 'Browse through verified listings of apartments, houses, and rooms in your desired location.',
    icon: <HomeIcon sx={{ fontSize: 40 }} />,
    path: '/rentals',
  },
  {
    title: 'Connect with Roommates',
    description: 'Find compatible roommates based on lifestyle, preferences, and location matches.',
    icon: <PeopleIcon sx={{ fontSize: 40 }} />,
    path: '/roommates',
  },
  {
    title: 'Legal Resources',
    description: 'Access comprehensive guides and resources about rental laws and tenant rights.',
    icon: <GavelIcon sx={{ fontSize: 40 }} />,
    path: '/legal',
  },
];

const GOOGLE_MAPS_API_KEY = 'AIzaSyABl8lts6BmCwMfw-cVqa1Xy3xDxoEJhUg';

function Home() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => setSearchInput(e.target.value);

  const handleSearch = async (e) => {
    e.preventDefault();
    await rentalSearch(searchInput, navigate, setLoading);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          py: 8,
          mb: 6,
          minHeight: '500px',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/images/homepage_2.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 1,
            zIndex: -1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            zIndex: -1,
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            component="h1"
            variant="h2"
            align="center"
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              mb: 3
            }}
          >
            Find Your Next Home
          </Typography>
          <Typography 
            variant="h5" 
            align="center" 
            paragraph
            sx={{ 
              color: 'white',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              mb: 4
            }}
          >
            The smart way to find rentals and roommates in your area
          </Typography>
          
          {/* Search Box */}
          <Paper
            component="form"
            onSubmit={handleSearch}
            sx={{
              p: '2px 4px',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: 600,
              mx: 'auto',
              mt: 4,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            <TextField
              fullWidth
              placeholder="Enter a city, neighborhood, or address"
              variant="standard"
              value={searchInput}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                disableUnderline: true,
              }}
              sx={{ ml: 1, flex: 1 }}
            />
            <Button 
              variant="contained" 
              sx={{ px: 4, m: 1, bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
              onClick={handleSearch}
              type="submit"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Promo banner temporarily hidden. Restore the old Limited Time Offer Banner here if needed. */}
      {/*
      <Container maxWidth="lg" sx={{ mb: 6 }}>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
            color: 'white',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 100,
              height: 100,
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 60,
              height: 60,
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
            }
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              mb: 2,
              position: 'relative',
              zIndex: 1
            }}
          >
            🎁 2-Week Limited Time Offer!
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              opacity: 0.95,
              position: 'relative',
              zIndex: 1
            }}
          >
            Get a $20 Gift Card after landlord confirms your lease signing!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              opacity: 0.9,
              position: 'relative',
              zIndex: 1
            }}
          >
            Sign a lease through BunnyRent and receive your reward once confirmed
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/auth')}
            sx={{
              bgcolor: 'white',
              color: '#ff6b6b',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
              position: 'relative',
              zIndex: 1
            }}
          >
            Get Started Now
          </Button>
        </Paper>
      </Container>
      */}

      <FeaturedListingsSection />

      {/* Features Section 改为 About Us 左右布局 */}
      <Box sx={{ width: '100vw', mt: 6, mb: 6 }}>
        <Grid container spacing={0} alignItems="stretch" sx={{ minHeight: '600px', width: '100%' }}>
          {/* 左侧图片 - 左半屏幕居中 */}
          <Grid 
            item 
            xs={12} 
            md={6}
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              order: { xs: 2, md: 1 },
              width: { md: '50vw' },
              px: { xs: 3, md: 0 },
              py: { xs: 4, md: 6 },
              pr: { md: 1.5, lg: 2 }
            }}
          >
            <Box 
              sx={{ 
                width: '100%', 
                maxWidth: { xs: '400px', md: '500px', lg: '600px', xl: '700px' },
                height: { xs: '350px', md: '500px', lg: '550px', xl: '600px' },
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center'
              }}
            >
              <img 
                src="/images/homepage_1.png" 
                alt="About BunnyRent" 
                style={{ 
                  width: '100%', 
                  height: '100%',
                  objectFit: 'cover', 
                  borderRadius: 20,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                }} 
              />
            </Box>
          </Grid>
          
          {/* 右侧 About Us - 右半屏幕居中 */}
          <Grid 
            item 
            xs={12} 
            md={6}
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center',
              alignItems: 'center',
              width: { md: '50vw' },
              px: { xs: 3, md: 0 },
              py: { xs: 4, md: 6 },
              pl: { md: 1.5, lg: 2 },
              order: { xs: 1, md: 2 }
            }}
          >
            <Box 
              sx={{ 
                width: '100%', 
                maxWidth: { xs: '100%', md: '500px', lg: '600px', xl: '650px' },
                textAlign: { xs: 'center', md: 'left' },
                px: { md: 4, lg: 5, xl: 6 }
              }}
            >
              <Typography 
                variant="h3" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  mb: 3,
                  color: 'text.primary',
                  fontSize: { xs: '1.8rem', md: '2rem', lg: '2.3rem', xl: '2.5rem' }
                }}
              >
                About Us
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  mb: 4, 
                  lineHeight: 1.7,
                  fontSize: { xs: '1rem', md: '1.1rem', lg: '1.15rem', xl: '1.2rem' },
                  fontWeight: 400
                }}
              >
                We are a group of USC students who built this platform to make apartment hunting easier, clearer, and more trustworthy. Our role is strictly neutral — we simply provide information and connect you with options. We do not take part in lease negotiations or the signing process.
                We collaborate with a variety of apartment communities and property managers to share reliable, transparent, and up-to-date listings. Whether you’re a new student, a current resident, or a young professional looking for your next home, our goal is to help you explore your options with confidence.
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 4, 
                  mt: 6, 
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  sx={{ 
                    minWidth: { xs: 160, md: 180, xl: 220 }, 
                    fontWeight: 600,
                    py: { xs: 1.5, md: 2, xl: 2.5 },
                    px: { xs: 3, md: 4, xl: 5 },
                    borderRadius: 3,
                    fontSize: { xs: '1rem', md: '1.1rem', xl: '1.3rem' },
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-3px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => navigate('/rentals')}
                  startIcon={<HomeIcon />}
                >
                  Find Rentals
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ 
                    minWidth: { xs: 160, md: 180, xl: 220 }, 
                    fontWeight: 600,
                    py: { xs: 1.5, md: 2, xl: 2.5 },
                    px: { xs: 3, md: 4, xl: 5 },
                    borderRadius: 3,
                    fontSize: { xs: '1rem', md: '1.1rem', xl: '1.3rem' },
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.1)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => navigate('/roommates')}
                  startIcon={<PeopleIcon />}
                >
                  Find Roommates
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Contact Section */}
      <Box 
        sx={{ 
          mt: 8, 
          mb: 6,
          py: 6,
          bgcolor: 'grey.50',
          borderRadius: 2
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" align="center" gutterBottom>
            Contact Us
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Have questions? We're here to help!
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
              >
                <EmailIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Email
                </Typography>
                <Link href="mailto:bunnyrent987@gmail.com" color="inherit">
                  bunnyrent987@gmail.com
                </Link>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default Home; 